<script setup lang="ts">
import UiText from './UiText.vue';
import WarpShader from './WarpShader.vue';
import ResponsiveImage from './ResponsiveImage.vue';
import { computed } from 'vue';

/**
 * 🌟 AMBIENT FABRIC BACKGROUND 🌟
 *
 * This component creates a sophisticated animated fabric-like background using the WarpShader.
 * The visual style is global and consistent across all usages.
 *
 * Params can be customized via the debug panel when the `warpShaderDebug` feature flag is enabled.
 * Toggle the debug panel with Ctrl+Shift+. (Cmd+Shift+. on Mac).
 */

export interface AmbientBackgroundSettings {
  aria_label?: string;
  class?: string;
  overlayHeading?: string;
}

const ENABLE_WARP_SHADER = false;

// Background image URL
const BACKGROUND_URL = 'https://cdn.shopexperts.com/media/public/global-background.jpg';

export interface WarpShaderParams {
  colors: string[];
  speed: number;
  swirl: number;
  swirlIterations: number;
  softness: number;
  distortion: number;
  proportion: number;
  shapeScale: number;
  shape: 'checks' | 'stripes' | 'edge';
  rotation: number;
  scale: number;
}

interface AmbientBackgroundProps {
  settings?: AmbientBackgroundSettings;
  /** Optional warp shader params. If not provided, will use defaults */
  params?: WarpShaderParams;
  /** Optional function to get params dynamically (e.g., from a debug store) */
  getParams?: () => WarpShaderParams;
}

const props = defineProps<AmbientBackgroundProps>();

// Default params (matching warpShaderDefaults.json)
const DEFAULT_PARAMS: WarpShaderParams = {
  colors: ['#909dd0', '#a3cce6', '#e0eaff', '#c7dcff'],
  speed: 1,
  swirl: 1,
  swirlIterations: 2,
  softness: 0.1,
  distortion: 0,
  proportion: 1,
  shapeScale: 0.25,
  shape: 'checks',
  rotation: 270,
  scale: 1,
};

// Get params: props.params > getParams function > defaults
const params = computed(() => {
  if (props.params) {
    return props.params;
  }

  if (props.getParams) {
    try {
      return props.getParams();
    } catch {
      // Fallback to defaults if getParams fails
    }
  }

  return DEFAULT_PARAMS;
});
</script>

<template>
  <WarpShader v-if="ENABLE_WARP_SHADER" :colors="params.colors" :speed="params.speed" :swirl="params.swirl"
    :swirl-iterations="params.swirlIterations" :softness="params.softness" :distortion="params.distortion"
    :proportion="params.proportion" :shape-scale="params.shapeScale" :shape="params.shape" :rotation="params.rotation"
    :scale="params.scale" :class="props.settings?.class || 'w-full h-full'">
    <!-- Optional overlay heading for semantics -->
    <div v-if="props.settings?.overlayHeading" class="pointer-events-none">
      <UiText as="h2" variant="h2" class="sr-only" :text="props.settings?.overlayHeading" />
    </div>

    <!-- Slot for overlay content -->
    <slot />
  </WarpShader>

  <div v-else class="ambient-background-container isolate w-full h-full dark text-se-white relative overflow-hidden">
    <!-- Responsive background image -->
    <ResponsiveImage :src="BACKGROUND_URL" background loading="eager" fetchpriority="high"
      class="absolute inset-0 w-full h-full object-cover object-center" />

    <!-- Optional overlay heading for semantics -->
    <div class="relative h-full">
      <div v-if="props.settings?.overlayHeading" class="pointer-events-none">
        <UiText as="h2" variant="h2" class="sr-only" :text="props.settings?.overlayHeading" />
      </div>

      <!-- Slot for overlay content (sits on top of the background image) -->
      <slot />
    </div>
  </div>
</template>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
